import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  CCloseButton,
  CSidebar,
  CSidebarBrand,
  CSidebarFooter,
  CSidebarHeader,
  CSidebarToggler,
} from '@coreui/react'

import { AppSidebarNav } from './AppSidebarNav'
import { setSidebarShow, setSidebarUnfoldable } from '../../store/slices/uiSlice'
import { useTenant } from '../../contexts/TenantContext'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

// sidebar nav config
import navigation from '../../_nav'

// Generate initials from academy name
const getInitials = (name) => {
  if (!name) return 'E';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Generate a consistent color based on name
const getColorFromName = (name) => {
  if (!name) return '#667eea';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];
  return colors[Math.abs(hash) % colors.length];
};

const AppSidebar = () => {
  const dispatch = useDispatch()
  const unfoldable = useSelector((state) => state.ui?.sidebarUnfoldable)
  const sidebarShow = useSelector((state) => state.ui?.sidebarShow)
  const { currentTenant } = useTenant()

  // Determine what to show: tenant logo > tenant name initials > EDUNA logo
  const renderBrand = () => {
    if (currentTenant?.logo) {
      // Show tenant logo
      return (
        <>
          <div className="sidebar-brand-full d-flex align-items-center px-2">
            <img 
              src={currentTenant.logo} 
              alt={currentTenant.name} 
              style={{ 
                maxHeight: '40px', 
                maxWidth: '160px', 
                objectFit: 'contain' 
              }} 
            />
          </div>
          <div className="sidebar-brand-narrow">
            <img 
              src={currentTenant.logo} 
              alt={currentTenant.name} 
              style={{ 
                maxHeight: '32px', 
                maxWidth: '32px', 
                objectFit: 'contain' 
              }} 
            />
          </div>
        </>
      );
    } else if (currentTenant?.name) {
      // Show academy name with initials
      const initials = getInitials(currentTenant.name);
      const bgColor = getColorFromName(currentTenant.name);
      return (
        <>
          <div className="sidebar-brand-full d-flex align-items-center gap-2 px-2">
            <div 
              style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: '8px', 
                background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '14px',
                flexShrink: 0
              }}
            >
              {initials}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div 
                style={{ 
                  fontWeight: '600', 
                  fontSize: '14px',
                  color: '#1f2937',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '130px'
                }}
              >
                {currentTenant.name}
              </div>
              {currentTenant.slogan && (
                <div 
                  style={{ 
                    fontSize: '11px', 
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '130px'
                  }}
                >
                  {currentTenant.slogan}
                </div>
              )}
            </div>
          </div>
          <div className="sidebar-brand-narrow">
            <div 
              style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '6px', 
                background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '12px'
              }}
            >
              {initials}
            </div>
          </div>
        </>
      );
    } else {
      // Fallback to EDUNA branding
      return (
        <>
          <div className="sidebar-brand-full px-3">
            <h4 className="mb-0 fw-bold text-primary">
              EDUNA
            </h4>
            <small className="text-muted">Education Platform</small>
          </div>
          <div className="sidebar-brand-narrow">
            <h3 className="mb-0 fw-bold text-primary">E</h3>
          </div>
        </>
      );
    }
  };

  return (
    <CSidebar
      className="border-end"
      colorScheme="light"
      position="fixed"
      unfoldable={unfoldable}
      visible={sidebarShow}
      onVisibleChange={(visible) => {
        dispatch(setSidebarShow(visible))
      }}
    >
      <CSidebarHeader className="border-bottom">
        <CSidebarBrand to="/">
          {renderBrand()}
        </CSidebarBrand>
        <CCloseButton
          className="d-lg-none"
          onClick={() => dispatch(setSidebarShow(false))}
        />
      </CSidebarHeader>
      <SimpleBar style={{ maxHeight: '100%', height: 'calc(100vh - 112px)' }}>
        <AppSidebarNav items={navigation} />
      </SimpleBar>
      <CSidebarFooter className="border-top d-none d-lg-flex">
        <CSidebarToggler
          onClick={() => dispatch(setSidebarUnfoldable(!unfoldable))}
        />
      </CSidebarFooter>
    </CSidebar>
  )
}

export default React.memo(AppSidebar)